import { useMutation } from '@apollo/client';
import { OptimizerRunV2Type, OptimizerV2Preset } from '@generated/graphql';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Box, Button, Typography, useToast } from 'hephaestus/web';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  BULK_REBALANCE_ORG_MODE,
  BULK_REBALANCE_START_RUN_TYPES,
} from '../constants/policy';
import { useBulkRebalanceDriftContext } from '../hooks/useBulkRebalanceDriftContext';
import { bulkRebalanceDetailPath } from '../paths';
import { RUN_PORTFOLIO_OPTIMIZER_V2 } from '../queries/optimizerRunsV2.queries';

export type StartBulkRebalanceModalProps = {
  open: boolean;
  onClose: () => void;
};

/** Cash runs opt into HS scoped deploy/raise. Drift stays full QP. */
export const shouldEnableScopedRebalanceModes = (
  type: OptimizerRunV2Type,
): boolean => type === OptimizerRunV2Type.Transfer;

export const StartBulkRebalanceModal = ({
  open,
  onClose,
}: StartBulkRebalanceModalProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const { activeOrganization } = useContext(UserContext);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { enqueueToast } = useToast();
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<OptimizerRunV2Type>(
    OptimizerRunV2Type.Transfer,
  );
  const isDriftRun = selectedType === OptimizerRunV2Type.Drift;
  const { isDriftFeatureEnabled } = useBulkRebalanceDriftContext();

  const handleOpenTradeSettings = () => {
    if (loading) {
      return;
    }

    onClose();
    navigate('/settings/trades');
  };

  const [runPortfolioOptimizerV2, { loading }] = useMutation(
    RUN_PORTFOLIO_OPTIMIZER_V2,
    {
      onCompleted: (data) => {
        const runId = data?.runPortfolioOptimizerV2?.optimizerRun?.id;
        if (!runId) {
          return;
        }

        sendAnalytic(ovAnalyticsEvents.portfolioOptimizerRunComplete, {
          optimizerRunId: runId,
          organizationId: activeOrganization.id,
          timestamp: new Date().toISOString(),
        });

        onClose();
        navigate(bulkRebalanceDetailPath(runId));
      },
      onError: (error) => {
        sendAnalytic(ovAnalyticsEvents.portfolioOptimizerRunError, {
          organizationId: activeOrganization.id,
          selectedType,
          selectedMode: BULK_REBALANCE_ORG_MODE,
          errorMessage: error.message,
          timestamp: new Date().toISOString(),
        });
        enqueueToast(error.message, { variant: 'error' });
      },
    },
  );

  const handleClose = () => {
    if (loading) {
      return;
    }

    sendAnalytic(ovAnalyticsEvents.portfolioOptimizerRunAbandon, {
      organizationId: activeOrganization.id,
      timestamp: new Date().toISOString(),
    });
    onClose();
  };

  const handleStart = () => {
    sendAnalytic(ovAnalyticsEvents.portfolioOptimizerRunInit, {
      organizationId: activeOrganization.id,
      selectedType,
      selectedMode: BULK_REBALANCE_ORG_MODE,
      timestamp: new Date().toISOString(),
    });

    runPortfolioOptimizerV2({
      variables: {
        input: {
          organizationId: activeOrganization.id,
          type: selectedType,
          preset: OptimizerV2Preset.V1Compatible,
          mode: BULK_REBALANCE_ORG_MODE,
          ...(shouldEnableScopedRebalanceModes(selectedType) && {
            scopedRebalanceModesEnabled: true,
          }),
        },
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='xs'
      data-testid='bulk-rebalance-start-modal'
    >
      <DialogTitle onClose={handleClose}>{t('startRun.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SelectField
            label={t('startRun.typeLabel')}
            value={selectedType}
            onChange={(event: { target: { value: OptimizerRunV2Type } }) =>
              setSelectedType(event.target.value)
            }
            disabled={loading}
            testId='bulk-rebalance-start-type'
          >
            {BULK_REBALANCE_START_RUN_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`runType.${type}`)}
              </MenuItem>
            ))}
          </SelectField>

          <Typography variant='bodySmall' color='textSecondary'>
            {t(`runTypeDescription.${selectedType}`)}
          </Typography>

          {isDriftRun ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                variant='labelSmall'
                color='textSecondary'
                data-testid='bulk-rebalance-start-drift-tolerance-note'
              >
                {t('startRun.driftToleranceNote')}
              </Typography>
              {isDriftFeatureEnabled ? (
                <Button
                  variant='text'
                  size='small'
                  onClick={handleOpenTradeSettings}
                  disabled={loading}
                  data-testid='bulk-rebalance-start-drift-settings-link'
                >
                  {t('startRun.changeDriftTolerance')}
                </Button>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogFooter>
        <Button
          variant='text'
          onClick={handleClose}
          disabled={loading}
          data-testid='bulk-rebalance-start-cancel'
        >
          {t('startRun.cancel')}
        </Button>
        <Button
          variant='contained'
          onClick={handleStart}
          disabled={loading}
          loading={loading}
          data-testid='bulk-rebalance-start-confirm'
        >
          {t('startRun.confirm')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
